'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface HeroSectionProps {
  className?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ className = '' }) => {
  const [currentHeadingIndex, setCurrentHeadingIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const headings = [
  { white: 'Enterprise AI That Delivers', blue: 'Measurable', white2: 'Results' },
  { white: 'Transform Customer Experience with', blue: 'AI-Powered', white2: 'Intelligence' },
  { white: 'Unify Your Business Operations with', blue: 'Intelligent', white2: 'Automation' }];


  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentHeadingIndex((prevIndex) => (prevIndex + 1) % headings.length);
        setIsVisible(true);
      }, 500);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className={`relative bg-gradient-to-br from-primary via-secondary to-brand-teal overflow-hidden ${className}`}>
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-conversion rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
      </div>

      <div className="container mx-auto px-4 pt-6 pb-16 lg:pt-10 lg:pb-24 relative z-10">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          {/* Left Content */}
          <div className="text-white space-y-8">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20">
              <Icon name="SparklesIcon" size={20} variant="solid" className="text-accent" />
              <span className="font-body text-sm font-medium">25+ Years of Transformation Excellence</span>
            </div>

            {/* Main H1 Heading */}
            <div className="space-y-3">
              <h3 className={`font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-tight h-[160px] lg:h-[180px] flex items-start transition-opacity duration-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                <span className="block w-full">
                  <span className="text-white">{headings[currentHeadingIndex].white}</span>
                  {' '}
                  <span className="text-[rgba(22,28,29,1)]">{headings[currentHeadingIndex].blue}</span>
                  {' '}
                  <span className="text-white">{headings[currentHeadingIndex].white2}</span>
                </span>
              </h3>
              
              {/* Indicator Dots */}
              <div className="flex items-center space-x-3 justify-start">
                {headings.map((_, index) =>
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentHeadingIndex ?
                  'w-8 bg-accent' : 'w-2 bg-white/40'}`
                  } />

                )}
              </div>
            </div>

            <p className="font-body text-lg lg:text-xl text-white/90 leading-relaxed">
              UnifyMax combines 25 years of industry expertise with cutting-edge AI to deliver seamless, intelligent customer experiences across telecom, banking, and retail sectors.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-conversion text-conversion-foreground rounded-lg font-cta text-base font-semibold hover:shadow-cta transition-all duration-300 transform hover:scale-105">

                <span>Talk to sales</span>
                <Icon name="ArrowRightIcon" size={20} variant="solid" />
              </Link>
              <Link
                href="#unifymax-techstack"
                className="inline-flex items-center justify-center space-x-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-cta text-base font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300">

                <span>Explore Products</span>
                <Icon name="CubeIcon" size={20} variant="outline" />
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-white" />
                <span className="font-body text-sm text-white/80">SOC 2 Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-white" />
                <span className="font-body text-sm text-white/80">ISO 27001</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="LockClosedIcon" size={24} variant="solid" className="text-white" />
                <span className="font-body text-sm text-white/80">GDPR Compliant</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="DocumentCheckIcon" size={24} variant="solid" className="text-white" />
                <span className="font-body text-sm text-white/80">HIPAA Compliant</span>
              </div>
            </div>
          </div>

          {/* Right Content - Interactive Stats */}
          <div className="relative lg:pt-8">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 lg:p-10 border border-white/20 shadow-2xl">
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-white/20">
                  <h3 className="font-display font-semibold text-xl lg:text-2xl text-white">Transformation Impact</h3>
                  <Icon name="ChartBarIcon" size={24} variant="solid" className="text-accent" />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="ArrowTrendingUpIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-display font-bold text-3xl lg:text-4xl text-white">48%</span>
                    </div>
                    <p className="font-body text-sm text-white/80">Reduction in Agent Error</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="ClockIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-display font-bold text-3xl lg:text-4xl text-white">60%</span>
                    </div>
                    <p className="font-body text-sm text-white/80">Queries Self Served</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="CurrencyDollarIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-display font-bold text-3xl lg:text-4xl text-white">$400,000</span>
                    </div>
                    <p className="font-body text-sm text-white/80">Average Annual Savings</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon name="UsersIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-display font-bold text-3xl lg:text-4xl text-white">100+</span>
                    </div>
                    <p className="font-body text-sm text-white/80">Enterprise Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 60L60 55C120 50 240 40 360 35C480 30 600 30 720 32.5C840 35 960 40 1080 42.5C1200 45 1320 45 1380 45L1440 45V60H1380C1320 60 1200 60 1080 60C960 60 840 60 720 60C600 60 480 60 360 60C240 60 120 60 60 60H0Z" fill="rgb(250, 251, 252)" />
        </svg>
      </div>
    </section>);

};

export default HeroSection;